/* jshint node:true */
'use strict';

// Date comparison taken from
// http://stackoverflow.com/questions/492994/compare-two-dates-with-javascript

const moment = require('moment-timezone');
const selectn = require('selectn');

// date time formatting related settings
const dateTime = {
    'dateFormats': {
        'time1': '',
        'date1': 'D MMM, YYYY',
        'date2': 'D/M/YYYY',
        'date3': 'DD/MM/YYYY',
        'date4': 'DDMMYYYY',
        'date5': 'Do MMMM YYYY',
        'date6': 'dddd, Do MMMM YYYY',
        'date7': 'ddd, D MMM YYYY',
        'date8': 'D/M',
        'date9': 'ddd',
        'date10': 'dddd, MMMM Do',
        'date11': 'MMMM YYYY',
        'date12': 'YYYYMMDD',
        'date13': 'MMMM Do, YYYY',
        'date14': 'M/D',
        'date15': 'dddd, D [de] MMMM', // Brazil Edition
        'date16': 'dddd, Do MMMM',
        'dateTime1': 'h:mm A, MMMM D, YYYY',
        'dateTime2': 'h:mm A',
        'dateTime3': 'H:mm',
        'dateTime4': 'MM/DD/YY HH:mm z',
        'dateTime5': 'dddd, h:mm A',
        'dateTime6': 'M/D h:mm A',
        'dateTime7': 'dddd, MMMM DD YYYY - hh:mm A'
    },
    'dayNamesLong': 'Sunday Monday Tuesday Wednesday Thursday Friday Saturday'.split(' '),
    'dayNamesMedium': 'Sun Mon Tue Wed Thu Fri Sat'.split(' '),
    'dayNamesShort': 'Su Mo Tu We Th Fr Sa'.split(' '),
    'monthNamesLong': 'January February March April May June July August September October November December'.split(' '),
    'monthNamesShort': 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ')
};

export default {

    getTimezone: function() {
        return moment.tz.guess() || 'UTC';
    },

    dateToKey: function (oDate) {
        return this.dateFormat(oDate, 'date12')
    },

    paramToCalendarDatesString: function (sParam) {
        if (!sParam) {
            return sParam;
        }
        return `${sParam.substr(0, 4)}-${sParam.substr(4, 2)}-${sParam.substr(6, 2)}`;
    },

    paramToDate: function (sParam) {
        const dateStr = this.paramToCalendarDatesString(sParam);

        if (!dateStr) {
            return dateStr;
        }

        return moment(dateStr).toDate();
    },

    dateDayNamesMedium: function () {
        return dateTime.dayNamesMedium
    },

    dateFormat: function (ISODate, dateFormat, showTimezone) {
        if (ISODate === undefined) {
            return ISODate
        }

        if (dateFormat === undefined) {
            dateFormat = 'date6'
        }

        showTimezone = showTimezone || false;

        const formatsMap = dateTime.dateFormats;
        const utcMoment = moment(ISODate);//, moment.ISO_8601
        const localMoment = moment(utcMoment.utc().toDate());

        dateFormat = formatsMap[dateFormat];

        const dateString = localMoment.format(dateFormat);
        //console.log(localMoment.format('z'))
        //window.moment().tz(window.jstz.determine().name()).format('z')
        //if ($(element).hasClass('time') && showTimezone === 'true') {
        //    var currentTimezone = espn_ui.Helpers.timezones.getCurrentTimezone()
        //    dateString += ' ' + espn_ui.Behaviors.date_timezone.replace(currentTimezone)
        //}

        return dateString
    },

    /**
     * Returns a formatted date string for events following the criteria below:
     * 1) Same day - 5:00 PM
     * 2) Next day - Tomorrow, 5:00 PM
     * 3) Between 2-6 days - Thursday, 5:00 PM
     * 4) past date & 7+ days - 5/29, 5:00 PM
     *
     * @param {ISODate} - Standardized date object
     * @param {dateFormat} - Format of output string
     * @param {timeZone} - Optional user timezone
     * @param {nextDayString} - i18n string representation for the the next day
     */
    eventDateFormat: function (ISODate, dateFormat, timeZone, nextDayString = 'Tomorrow') {
        if (ISODate === undefined) {
            return ISODate;
        }

        const { dateFormats } = dateTime;
        const currentDateFormat = dateFormat || 'dateTime2';
        const eventDate = timeZone ? moment.tz(ISODate, timeZone) : moment(ISODate);
        const momentInstance = timeZone ? moment.tz(timeZone) : moment();
        const tomorrow = momentInstance.clone().add(1, 'day');
        const sevenDaysFromToday = momentInstance.clone().add(7, 'days');
        const eventDateFormatted = eventDate.format(dateFormats[currentDateFormat]);

        // Same day event
        if (eventDate.isSame(momentInstance, 'day')) {
            return eventDateFormatted; // Output: 5:00 PM
        }
        // Tomorrow
        if (eventDate.isSame(tomorrow, 'day')) {
            return `${nextDayString}, ${eventDateFormatted}`; // Output: Tomorrow, 5:00 PM; i18n supported
        }
        // 2-6 day range
        if (eventDate.isAfter(tomorrow, 'day') && eventDate.isBefore(sevenDaysFromToday, 'day')) {
            return eventDate.format(dateFormats['dateTime5']); // Output: Friday, 5:00 PM
        }
        // Past event and 7+ days (base case)
        return eventDate.format(dateFormats['dateTime6']); // Output: 06/13 5:00 PM
    },

    /**
     * Converts the date in d to a date-object using moment-timezone
     * @param  {String/object} d date in string/Object
     * @return {Object}          date-object
     */
    dateToObject: function (d) {
        return moment(d).toDate()
    },

    /**
     * Compare two dates (could be of any type supported by the convert
     * function above) and returns:
     *  -1 : if a < b
     *   0 : if a = b
     *   1 : if a > b
     * NaN : if a or b is an illegal date
     * NOTE: The code inside isFinite does an assignment (=).
     *
     * @param  {String/object} a date in string/Object
     * @param  {String/object} b date in string/Object
     * @return {Number}        as described above
     **/
    dateCompare: function (a, b) {
        return (
            isFinite(a = this.dateToObject(a).valueOf()) &&
            isFinite(b = this.dateToObject(b).valueOf()) ?
            (a > b) - (a < b) :
                NaN
        )
    },

    /**
     * Checks if date in d is between dates in start and end.
     * Returns a boolean or NaN:
     *    true  : if d is between start and end (inclusive)
     *    false : if d is before start or after end
     *    NaN   : if one or more of the dates is illegal.
     * NOTE: The code inside isFinite does an assignment (=).
     *
     * @param {String/object} d date in string/Object
     * @param {String/Object} start startdate
     * @param {String/Object} end startdate
     * @return {Boolean/NaN}  As described above
     **/
    dateInRange: function (d, start, end) {
        return (
            isFinite(d = this.dateToObject(d).valueOf()) &&
            isFinite(start = this.dateToObject(start).valueOf()) &&
            isFinite(end = this.dateToObject(end).valueOf()) ?
            start <= d && d <= end :
                NaN
        )
    },

    /**
     * Convert date/time as per the passed options of timezone and formatting
     * @param  {String} d date  String in ISO 8601 format
     * @param  {Object} options date formatting options
     * @return {String}         formatted time as per the options
     */
    convertDateTime: function (d, options) {
        // default return type
        let displayTime = ''

        /**
         * default formatting options
         * UTC as time zone - 'Europe/London'
         * 24HR time format - HH:mm z
         */
        let timeZone = 'Europe/London'
        let timeFormat = 'h:mm A'
        // Override default options if options input has valid properties
        if (typeof selectn('timeZone', options) !== 'undefined') {
            timeZone = options.timeZone
        }

        if (typeof selectn('timeFormat', options) !== 'undefined') {
            timeFormat = options.timeFormat
        }

        if (typeof selectn('dateFormat', options) !== 'undefined') {
            const formatsMap = dateTime.dateFormats;
            const { dateFormat } = options;
            if (formatsMap[dateFormat]) {
                timeFormat = formatsMap[dateFormat];
            }
        }

        // Convert to moment object
        let startTime = moment(d)
        // Format the date only if valid
        if (startTime.isValid() === true) {
            displayTime = startTime.tz(timeZone).format(timeFormat)
            //console.log('original, convertDateTime', d, displayTime)
        }
        return displayTime
    },

    getTimezoneParam: function() {
        const tz = moment().utcOffset();
        const hour = Math.abs(Math.floor(tz / 60));
        const min = Math.abs(tz % 60);
        return `UTC${tz >= 0 ? '+' : '-'}${('0' + hour).slice(-2)}${('0' + min).slice(-2)}`;
    },

    /**
     * Convert date string from v1/listings API to the valid ISO string
     * @param  {String} str A date string to convert
     * @return {String} ISO 8601 date string
     */
    toISOString: function (str = '') {
        return str.replace(/GMT$/, 'Z')
                  .replace(/EST$/, '-05:00')
                  .replace(/EDT$/, '-04:00');
    },

    /**
     * Convert date to ISO string with minute precision
     * @param  {Date} Standardized date object
     * @return {String} ISO 8601 date string with minute precision
     */
    getISOStringWithMinPrecision: function (date = new Date()) {
        return date.toISOString().replace(/:\d+\.\d+Z$/, '');
    },

    /**
     * Formats a standardized date by the format specified with optional timezone
     * @param {date} - Standardized date object
     * @param {dateFormat} - Format of output string
     * @param {timeZone} - Optional user timezone
     */
    getDateTimeString: function(date, dateFormat, timeZone, type, nextDayString) {
        if (type === 'schedule-event') {
            return this.eventDateFormat(date, dateFormat, timeZone, nextDayString);
        }

        if (timeZone) {
            return this.convertDateTime(date, { timeZone, dateFormat });
        }
        return this.dateFormat(date, dateFormat);
    }
}



// WEBPACK FOOTER //
// ./app/lib/format/dateFormat.js